import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { getActiveDevice } from '@/input/active-device';
import { t } from '@/i18n';
import { sparkleGlyph, swapGlyph } from './hangar-icons';
import { buildPauseButton } from './pause-button';
import { buildCompactCard, buildCompactReroll } from './shop-compact-cards';
import { buildCompactHangar } from './shop-compact-hangar';
import {
  CARD_C, CONTINUE_BTN, FOOT_H, FOOT_Y, MAIN_Y, MENU_BTN, PAGES, REROLL_TILE, TAB, tabX,
} from './shop-compact-layout';
import { buildCompactStatus } from './shop-compact-status';
import type { BayParts } from './shop-compose';
import { buildPromptDock } from './shop-prompt';
import type { FocusItem, ShopPage, ShopState } from './shop-types';
import { chamferPanel, label, targetBrackets } from './widgets';

/*
 * COMPACT (phone) UPGRADE BAY (polish 1, docs/06): glyph tabs CARDS |
 * HANGAR | STATUS over one page; the header keeps hull, armor and the
 * wallet in sight; the footer shows the focused item's ONE action and the
 * highlighted ▶ CONTINUE. Geometry: shop-compact-layout.ts.
 */

const VITALS_X = 222;
const ARMOR_X = 334;
const WALLET_RIGHT = MENU_BTN.x - 10;
const VITAL_SIZE = 15;
const WALLET_SIZE = 18;

/** Tab glyphs — no words (4-year-old rule). */
function tabGlyph(g: Graphics, page: ShopPage, color: number): void {
  if (page === 'cards') {
    // Two cards fanned: the back one outlined, the front one solid
    g.roundRect(-15, -13, 18, 25, 3).stroke({ color, width: 2, alpha: 0.6 });
    g.roundRect(-5, -11, 18, 25, 3).fill(color);
    g.circle(4, -1, 4).fill(0x0f0c1a);
  } else if (page === 'status') {
    // Ship chevron: "your ship"
    g.poly([15, 0, -11, -12, -5, 0, -11, 12]).fill(color);
  }
}

function buildTab(s: ShopState, page: ShopPage, active: boolean, selected: boolean): Container {
  const c = new Container();
  const color = active ? COLORS.uiAccent : 0x8a9099;
  c.addChild(chamferPanel(new Graphics(), TAB.w, TAB.h, {
    fill: active ? 0x2a2440 : 0x161222, stroke: active ? COLORS.uiAccent : 0x2a2440,
    strokeWidth: active ? 3 : 2, notch: 10, plain: true,
  }));
  if (page === 'hangar') {
    // The weapon in socket 1 stands for "your weapons"
    const id = s.equipped[0];
    if (id) {
      const glyph = new Sprite(getTexture(weaponGlyphKey(id)));
      glyph.anchor.set(0.5);
      glyph.scale.set(0.5);
      glyph.position.set(TAB.w / 2, TAB.h / 2);
      glyph.alpha = active ? 1 : 0.7;
      c.addChild(glyph);
    }
    // News waiting in the hangar: ⇄ crate (orange) beats ✦ new weapon
    if (s.pending) c.addChild(swapGlyph(new Graphics(), TAB.w - 12, 11, 7, COLORS.dangerOrange));
    else if (s.fresh.length > 0) c.addChild(sparkleGlyph(new Graphics(), TAB.w - 11, 11, 7, COLORS.playerYellow));
  } else {
    const g = new Graphics();
    tabGlyph(g, page, color);
    g.position.set(TAB.w / 2, TAB.h / 2);
    c.addChild(g);
  }
  if (selected) c.addChild(targetBrackets(new Graphics(), TAB.w, TAB.h, COLORS.uiAccent, 10, 3));
  return c;
}

/** Header: tabs, hull + armor readouts, wallet, ‖ pause. */
function buildHeader(layer: Container, s: ShopState, page: ShopPage, p: BayParts): void {
  PAGES.forEach((pg, i) => {
    const item: FocusItem = { kind: 'tab', page: pg };
    const tab = buildTab(s, pg, pg === page, p.base.isSel(item));
    tab.position.set(tabX(i), 0);
    p.base.interactive(tab, item);
    layer.addChild(tab);
  });
  const player = s.ctx.player;
  const cy = TAB.h / 2;
  const g = new Graphics();
  g.roundRect(VITALS_X, cy - 8, 26, 16, 3).stroke({ color: COLORS.hudHp, width: 2 });
  g.roundRect(VITALS_X + 26, cy - 4, 3, 8, 1).fill(COLORS.hudHp);
  const pct = Math.max(0, Math.min(1, player.hp / player.maxHp));
  g.roundRect(VITALS_X + 3, cy - 5, 20 * pct, 10, 2).fill(COLORS.hudHp);
  g.poly([ARMOR_X + 9, cy - 10, ARMOR_X + 18, cy - 6, ARMOR_X + 18, cy + 2, ARMOR_X + 9, cy + 10,
    ARMOR_X, cy + 2, ARMOR_X, cy - 6]).fill(COLORS.hudArmor);
  layer.addChild(g);
  const hp = label(`${Math.round(player.hp)}/${player.maxHp}`, VITAL_SIZE, COLORS.hudHp, 1);
  hp.anchor.set(0, 0.5);
  hp.position.set(VITALS_X + 36, cy);
  const armor = label(`×${player.armor}`, VITAL_SIZE, COLORS.hudArmor, 1);
  armor.anchor.set(0, 0.5);
  armor.position.set(ARMOR_X + 24, cy);
  layer.addChild(hp, armor);

  let x = WALLET_RIGHT;
  for (const [tex, value, color] of [
    ['pickup/gem-red', s.red, COLORS.gemRed],
    ['pickup/gem-yellow', s.yellow, COLORS.gemYellow],
  ] as const) {
    const n = label(String(value), WALLET_SIZE, color, 1);
    n.anchor.set(1, 0.5);
    n.position.set(x, cy);
    const gem = new Sprite(getTexture(tex));
    gem.anchor.set(1, 0.5);
    gem.scale.set(0.6);
    gem.position.set(x - n.width - 3, cy);
    layer.addChild(n, gem);
    x -= n.width + gem.width + 16;
  }
  if (p.openMenu) {
    const menu = buildPauseButton(MENU_BTN.size, p.openMenu);
    menu.position.set(MENU_BTN.x, 0);
    layer.addChild(menu);
  }
}

/** ▶ CONTINUE — the screen's one always-highlighted way forward. */
function buildContinue(selected: boolean): Container {
  const c = new Container();
  const w = CONTINUE_BTN.w;
  const h = FOOT_H;
  c.addChild(chamferPanel(new Graphics(), w, h, {
    fill: selected ? 0x1d4a44 : 0x16332f, stroke: selected ? COLORS.uiAccent : 0x2e8c80,
    strokeWidth: selected ? 4 : 3, notch: 12,
  }));
  const txt = label(t('shop.continueBtn'), 17, 0xffffff, 2);
  const tri = new Graphics().poly([0, -11, 18, 0, 0, 11]).fill(COLORS.uiAccent);
  const rowW = 28 + txt.width;
  const scale = rowW > w - 20 ? (w - 20) / rowW : 1;
  tri.position.set(w / 2 - (rowW * scale) / 2, h / 2);
  txt.anchor.set(0, 0.5);
  txt.scale.set(scale);
  txt.position.set(tri.x + 28 * scale, h / 2);
  c.addChild(tri, txt);
  if (selected) c.addChild(targetBrackets(new Graphics(), w, h, COLORS.uiAccent, 12, 4));
  return c;
}

/** One frame of the compact bay. */
export function composeCompact(
  layer: Container, s: ShopState, sel: FocusItem | undefined, page: ShopPage, p: BayParts,
): void {
  const { base } = p;
  buildHeader(layer, s, page, p);

  const body = new Container();
  body.position.set(0, MAIN_Y);
  layer.addChild(body);
  if (page === 'cards') {
    const countOf = (id: string): number => s.collection.find((e) => e.card.id === id)?.count ?? 0;
    s.offer.forEach((oc, i) => {
      const item: FocusItem = { kind: 'card', index: i };
      const card = buildCompactCard(oc, s, base.isSel(item), countOf(oc.card.id) + 1, () => p.toggleLock(i));
      card.position.set(i * (CARD_C.w + CARD_C.gap), 0);
      base.interactive(card, item);
      body.addChild(card);
    });
    const reroll = buildCompactReroll(s, base.isSel({ kind: 'reroll' }));
    reroll.position.set(REROLL_TILE.x, 0);
    base.interactive(reroll, { kind: 'reroll' });
    body.addChild(reroll);
  } else if (page === 'hangar') {
    body.addChild(buildCompactHangar(s, p.hangar));
  } else {
    body.addChild(buildCompactStatus(s, sel, base));
  }

  const dock = buildPromptDock(getActiveDevice(), p.prompt.primary, {
    scrap: p.prompt.scrap, scrapValue: p.prompt.scrapValue, info: false,
    reroll: s.yellow >= s.rerollPrice,
  });
  dock.position.set(0, FOOT_Y + FOOT_H / 2);
  layer.addChild(dock);
  const cont = buildContinue(base.isSel({ kind: 'continue' }));
  cont.position.set(CONTINUE_BTN.x, FOOT_Y);
  base.interactive(cont, { kind: 'continue' });
  layer.addChild(cont);
}
